/**
 * Create Word List Page
 * Form to create a new word list
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { apiRequest } from '@/api/client';
import { useNotification } from '@/hooks/useNotification';
import { sanitizeWordListData } from '@/utils/inputSanitizer';
import { getUserFriendlyError } from '@/utils/errorMessages';

export const CreateWordListPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [classes, setClasses] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    classId: '',
    listType: 'spelling',
    words: '',
  });

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await apiRequest<{ classes: any[] }>('get_teacher_classes', {
        userEmail: user?.email,
      });
      
      if (response.success && (response as any).classes) {
        setClasses((response as any).classes);
      }
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.name || !formData.classId || !formData.words) {
      showNotification('error', 'Please fill in all required fields');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Sanitize word list data
      const sanitized = sanitizeWordListData({
        listName: formData.name,
        classId: formData.classId,
        words: formData.words
      });

      if (sanitized.words.length === 0) {
        showNotification('error', 'Please add at least one valid word');
        return;
      }

      const response = await apiRequest('create_wordlist', {
        teacherEmail: user?.email,
        classId: sanitized.classId,
        name: sanitized.listName,
        listType: formData.listType,
        words: sanitized.words,
      });

      if (response.success) {
        showNotification('success', 'Word list created successfully');
        navigate('/wordlists');
      } else {
        showNotification('error', getUserFriendlyError(response, 'Word list'));
      }
    } catch (error) {
      console.error('Failed to create word list:', error);
      showNotification('error', getUserFriendlyError(error, 'Word list'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const wordCount = formData.words
    .split(/[\n,]+/)
    .map(w => w.trim())
    .filter(w => w.length > 0).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Create Word List</h1>
          <p className="text-slate-600 mt-2">Add vocabulary words for your class</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/wordlists')}>
          Cancel
        </Button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Word List Name *
              </label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Week 1 Spelling Words"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Class *
              </label>
              <select
                value={formData.classId}
                onChange={(e) => setFormData({ ...formData, classId: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
              >
                <option value="">Select a class</option>
                {classes.map((cls) => (
                  <option key={cls.classId} value={cls.classId}>
                    {cls.className} ({cls.gradeLevel})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                List Type *
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="listType"
                    value="spelling"
                    checked={formData.listType === 'spelling'}
                    onChange={(e) => setFormData({ ...formData, listType: e.target.value })}
                    className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-slate-700">Words (Spelling)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="listType"
                    value="phonics"
                    checked={formData.listType === 'phonics'}
                    onChange={(e) => setFormData({ ...formData, listType: e.target.value })}
                    className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-slate-700">Sounds (Phonics)</span>
                </label>
              </div>
              <p className="text-sm text-slate-500 mt-2">
                {formData.listType === 'spelling' 
                  ? 'Words will appear naturally in stories' 
                  : 'Multiple words containing these sound patterns will be included in stories'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {formData.listType === 'spelling' ? 'Words' : 'Sounds'} * ({wordCount} {formData.listType === 'spelling' ? 'word' : 'sound'}{wordCount !== 1 ? 's' : ''})
              </label>
              <textarea
                value={formData.words}
                onChange={(e) => setFormData({ ...formData, words: e.target.value })}
                placeholder={formData.listType === 'spelling' 
                  ? "Enter words (one per line or comma-separated)\nExample:\nadventure\nbeautiful\ncourage"
                  : "Enter sounds (one per line or comma-separated)\nExample:\nch\nsh\nth\noo\nai"}
                rows={10}
                className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                required
              />
              <p className="text-sm text-slate-500 mt-2">
                Enter one {formData.listType === 'spelling' ? 'word' : 'sound'} per line or separate with commas
              </p>
            </div>

            <div className="flex items-center gap-3 pt-4">
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Word List'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/wordlists')}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      </form>
    </div>
  );
};
